import { create } from 'zustand';
import { authService } from '../services/authService';
import { tokenManager, getApiError, type ApiError } from '../services/api';
import type { RegisterRequest, LoginRequest } from '@travel-planner/shared';

interface User {
  id: string;
  email: string;
  name: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: ApiError | null;

  // Actions
  login: (data: LoginRequest) => Promise<void>;
  /** Registers the user and returns the "check your email" message. Does NOT log in. */
  register: (data: RegisterRequest) => Promise<string>;
  /** Verifies the email from the link token and logs the user in on success. */
  verifyEmail: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,

  login: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.login(data);
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: getApiError(error),
        isLoading: false,
        isAuthenticated: false,
      });
      throw error;
    }
  },

  register: async (data) => {
    set({ isLoading: true, error: null });
    try {
      // Register no longer logs the user in — it sends a verification email.
      const { message } = await authService.register(data);
      set({ isLoading: false });
      return message;
    } catch (error) {
      set({
        error: getApiError(error),
        isLoading: false,
      });
      throw error;
    }
  },

  verifyEmail: async (token) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.verifyEmail(token);
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: getApiError(error),
        isLoading: false,
        isAuthenticated: false,
      });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authService.logout();
    } finally {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  checkAuth: async () => {
    if (!tokenManager.getAccessToken()) {
      set({ isAuthenticated: false, user: null });
      return;
    }

    set({ isLoading: true });
    try {
      const user = await authService.getMe();
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  clearError: () => set({ error: null }),
}));
